// src/pages/TenantEdit.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  ArrowLeft,
  Building2,
  Database,
  CalendarDays,
  Users,
  FileJson,
  BadgeInfo,
  Save,
  Ban,
  CheckCircle2,
} from "lucide-react";

// Helpers
function toYMD(input) {
  if (!input) return "";
  const d =
    typeof input === "string"
      ? new Date(input)
      : input instanceof Date
      ? input
      : new Date(String(input));
  if (isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function TenantEdit() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const passedRow = state?.row;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [row, setRow] = useState(null);

  // Form state
  const [planCode, setPlanCode] = useState("");
  const [maxUsers, setMaxUsers] = useState(0);
  const [expiresOn, setExpiresOn] = useState("");
  const [graceDays, setGraceDays] = useState(7);
  const [isSuspended, setIsSuspended] = useState(false);
  const [featuresJson, setFeaturesJson] = useState("");

  // Load tenant (from state or API)
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        let t = passedRow;
        if (!t) {
          try {
            const one = await axios.get(`https://superadmin.nissiindia.com/api/tenants/${tenantId}`);
            t = one.data;
          } catch {
            const list = await axios.get("https://superadmin.nissiindia.com/api/tenants");
            t = (list.data || []).find((x) => String(x.TenantId) === String(tenantId)) || null;
          }
        }
        if (!t) {
          Swal.fire("Not found", "Tenant could not be loaded.", "warning");
          navigate(-1);
          return;
        }
        setRow(t);
        setPlanCode(t.PlanCode ?? "");
        setMaxUsers(Number(t.MaxUsers ?? 0));
        setExpiresOn(toYMD(t.ExpiresOn));
        setGraceDays(Number(t.GraceDays ?? 7));
        setIsSuspended(Boolean(t.IsSuspended));
        setFeaturesJson(t.FeaturesJson ?? "");
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Unable to load tenant.", "error");
        navigate(-1);
        return;
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const onSave = async () => {
    if (!planCode || !expiresOn) {
      Swal.fire("Validation", "Plan and Expires On are required.", "info");
      return;
    }
    if (featuresJson && featuresJson.trim().length > 0) {
      try {
        JSON.parse(featuresJson);
      } catch {
        Swal.fire("Invalid JSON", "Please fix the Features JSON.", "warning");
        return;
      }
    }
    setSaving(true);
    try {
      await axios.post("https://superadmin.nissiindia.com/api/license/push", {
        tenantId: row.TenantId,
        planCode,
        maxUsers: Number(maxUsers || 0),
        expiresOn,
        graceDays: Number(graceDays || 0),
        isSuspended,
        featuresJson: featuresJson?.trim() || null,
        actor: "ui",
      });
      Swal.fire("Saved", "License updated.", "success");
      navigate(-1);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  const onToggleSuspend = async () => {
    try {
      await axios.post("https://superadmin.nissiindia.com/api/license/toggle-suspend", {
        tenantId: row.TenantId,
        actor: "ui",
      });
      const nextSusp = !isSuspended;
      setIsSuspended(nextSusp);
      Swal.fire("Done", nextSusp ? "Tenant suspended." : "Tenant unsuspended.", "success");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Toggle failed.", "error");
    }
  };

  // Loading screen (kept styled)
  if (loading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(1100px_500px_at_50%_-120px,rgba(15,23,42,0.10),transparent)] bg-slate-100">
        <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
          <SuperAdminNav />
          <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
            <div className="px-3 sm:px-4 lg:px-6 pt-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Edit Tenant</h1>
                    <p className="text-sm text-slate-600">Loading…</p>
                  </div>
                </div>
                <div className="rounded-xl bg-white/70 px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-200 backdrop-blur">
                  <Time />
                </div>
              </div>
            </div>
            <div className="px-3 sm:px-4 lg:px-6 py-6">
              <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-6 shadow-xl backdrop-blur">
                <p className="text-slate-600">Loading…</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!row) return null;

  const statusPill =
    row.EffectiveStatus === "ACTIVE"
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
      : row.EffectiveStatus === "BLOCKED"
      ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
      : "bg-amber-50 text-amber-700 ring-1 ring-amber-200";

  return (
    <div className="min-h-screen bg-[radial-gradient(1100px_500px_at_50%_-120px,rgba(15,23,42,0.10),transparent)] bg-slate-100">
      {/* Sidebar + Main (25% / 75%) */}
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        {/* MAIN: full-bleed width (no max-width), compact horizontal padding */}
        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          {/* Header bar */}
          <div className="px-3 sm:px-4 lg:px-6 pt-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">
                    Edit Tenant – {row.TenantCode}
                  </h1>
                  <p className="text-sm text-slate-600">
                    <span className="inline-flex items-center gap-2">
                      <span className="font-medium">Name:</span> {row.Name}
                    </span>
                    <span className="mx-2">•</span>
                    <span className="inline-flex items-center gap-2">
                      <Database className="h-4 w-4 text-slate-500" /> {row.DatabaseName}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <div className="rounded-xl bg-white px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-200">
                  <Time />
                </div>
              </div>
            </div>

            {/* Status chips */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs ${statusPill}`}>
                Current: {row.EffectiveStatus}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
                <Users className="h-4 w-4 text-indigo-600" /> Max: {row.MaxUsers ?? "—"}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
                <CalendarDays className="h-4 w-4 text-blue-600" /> Expires: {row.ExpiresOn ?? "—"}
              </span>
            </div>
          </div>

          {/* CONTENT: full-width grid (no max-w), tighter gaps on small screens */}
          <div className="px-3 sm:px-4 lg:px-6 py-6">
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
              {/* LEFT: License Settings (span 7/12 on xl) */}
              <div className="col-span-12 rounded-2xl border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur">
                <div className="flex items-center gap-2 border-b border-slate-200/70 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 rounded-t-2xl">
                  <BadgeInfo className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold text-slate-800">License Settings</h3>
                </div>

                <div className="p-4 lg:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Plan *</label>
                    <input
                      value={planCode}
                      onChange={(e) => setPlanCode(e.target.value)}
                      placeholder="STARTER / PRO"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Max Users</label>
                    <input
                      type="number"
                      min="0"
                      value={maxUsers}
                      onChange={(e) => setMaxUsers(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Expires On *</label>
                    <input
                      type="date"
                      value={expiresOn}
                      onChange={(e) => setExpiresOn(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Grace Days</label>
                    <input
                      type="number"
                      min="0"
                      value={graceDays}
                      onChange={(e) => setGraceDays(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">Suspended</label>
                    <select
                      value={isSuspended ? "1" : "0"}
                      onChange={(e) => setIsSuspended(e.target.value === "1")}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:outline-none"
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      NOTE: This updates the license payload. Use the button below to toggle immediately on the server.
                    </p>
                  </div>
                </div>

                <div className="border-t border-slate-200/70 px-4 lg:px-5 py-4 flex flex-col sm:flex-row gap-2 sm:justify-between rounded-b-2xl bg-white/60">
                  <p className="text-xs text-slate-600">Save will push license changes to the server.</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onSave}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-black focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? "Saving..." : "Save"}
                    </button>

                    <button
                      onClick={onToggleSuspend}
                      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-white shadow focus:outline-none focus:ring-2 ${
                        isSuspended
                          ? "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500/30"
                          : "bg-rose-600 hover:bg-rose-700 focus:ring-rose-500/30"
                      }`}
                    >
                      {isSuspended ? (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          Unsuspend (server)
                        </>
                      ) : (
                        <>
                          <Ban className="h-4 w-4" />
                          Suspend (server)
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => navigate(-1)}
                      className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-slate-900 ring-1 ring-slate-300 shadow hover:bg-slate-50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>

              
              {/* <div className="col-span-12 xl:col-span-5 rounded-2xl border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur">
                <div className="flex items-center gap-2 border-b border-slate-200/70 bg-gradient-to-r from-violet-50 to-purple-50 px-4 py-3 rounded-t-2xl">
                  <FileJson className="h-4 w-4 text-violet-600" />
                  <h3 className="text-sm font-semibold text-slate-800">Features JSON</h3>
                </div>

                <div className="p-4 lg:p-5">
                  <textarea
                    value={featuresJson}
                    onChange={(e) => setFeaturesJson(e.target.value)}
                    placeholder='{"modules":["inventory"]}'
                    className="min-h-[260px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 shadow-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-200"
                  />
                  <div className="mt-2 text-xs text-slate-600 flex items-center gap-2">
                    <BadgeInfo className="h-4 w-4 text-slate-500" />
                    JSON is optional; if provided, it must be valid.
                  </div>

                  
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Tenant</div>
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-500" />
                        {row.TenantCode}
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">DB</div>
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <Database className="h-4 w-4 text-slate-500" />
                        {row.DatabaseName}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200/70 px-4 lg:px-5 py-4 flex justify-end rounded-b-2xl bg-white/60">
                  <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-slate-900 ring-1 ring-slate-300 shadow hover:bg-slate-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                </div>
              </div> */}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
